import type ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import { MatterInsiderListExport, MatterInsiderRow } from 'types/matter-insider-list';

import { formatLocalMinute } from './insiderListFormat';

/** The template's column widths, A to H. */
const COLUMN_WIDTHS = [21.86, 25.43, 17.71, 21.86, 17.86, 17.86, 17.86, 16];

const FONT: Partial<ExcelJS.Font> = { name: 'Arial', size: 9 };
const THIN: Partial<ExcelJS.Border> = { style: 'thin' };
const BOXED: Partial<ExcelJS.Borders> = { top: THIN, left: THIN, bottom: THIN, right: THIN };
const HEADER_ROW = 10;
const TOP: Partial<ExcelJS.Alignment> = { vertical: 'top' };

/** Arial 9's line height in points. */
const LINE_HEIGHT = 12;

/**
 * Characters one line of a case-block value holds once merged across C to H. Counted in widths of the digit 0,
 * which Arial 9 prose beats, so the row it sizes errs on the tall side rather than clipping.
 */
const CASE_VALUE_CHARS = COLUMN_WIDTHS.slice(2).reduce((sum, width) => sum + width, 0);

/** The lines a case-block value wraps to, which the builder sizes its row from. */
function caseValueLines(text: string): number {
    return text.split('\n').reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / CASE_VALUE_CHARS)), 0);
}

/** The table's columns, A to H. H stays empty: the list keeps no additional-info field. */
const TABLE_LABELS = [
    'insider.first_name',
    'insider.last_name',
    'insider.work_phone',
    'insider.function_and_reason',
    'insider.access_obtained',
    'insider.access_ceased',
    'insider.id_code_or_birthday',
    'insider.additional_info',
];

function tableRow(row: MatterInsiderRow): string[] {
    return [
        row.firstName ?? '',
        row.lastName ?? '',
        row.workPhone ?? '',
        row.functionAndReason ?? '',
        formatLocalMinute(row.accessObtained),
        formatLocalMinute(row.accessCeased),
        row.personalId || row.dateOfBirth || '',
        '',
    ];
}

/**
 * Lays the insider list out on a new sheet the way the firm's paper template does: the firm and title on top, the
 * case block with its labels in A and values in C (merged on to H), the bordered table with one row per person, and
 * the footer with its labels in A and values in D. A label with no text in the export's language shows its
 * placeholder.
 */
export function buildInsiderListSheet(
    workbook: ExcelJS.Workbook,
    data: MatterInsiderListExport,
    printedAt: Date
): ExcelJS.Worksheet {
    const label = (placeholder: string): string => data.labels[placeholder] ?? placeholder;
    const sheet = workbook.addWorksheet('Insider list', { pageSetup: { orientation: 'portrait' } });
    sheet.columns = COLUMN_WIDTHS.map((width) => ({ width, style: { font: FONT } }));

    sheet.getCell('A1').value = data.lawFirmName ?? '';
    sheet.getCell('A1').font = { ...FONT, size: 10, bold: true };
    sheet.getCell('A2').value = label('insider.title');
    sheet.mergeCells('A1:C1');
    sheet.mergeCells('A2:C2');
    sheet.mergeCells('A3:C3');

    const caseBlock: [string, string][] = [
        ['insider.case_number_and_name', `${data.caseNumber} ${data.caseName}`],
        ['insider.insider_info', data.insiderInfo ?? ''],
        ['insider.insider_created_at', formatLocalMinute(data.listCreatedAt)],
        ['insider.insider_updated_at', formatLocalMinute(data.listUpdatedAt)],
        ['insider.print_date', formatLocalMinute(printedAt)],
    ];
    caseBlock.forEach(([placeholder, value], index) => {
        const row = sheet.getRow(4 + index);
        row.getCell(1).value = `${label(placeholder)}:`;
        row.getCell(1).alignment = TOP;
        row.getCell(3).value = value;
        row.getCell(3).alignment = { ...TOP, wrapText: true };
        sheet.mergeCells(row.number, 3, row.number, COLUMN_WIDTHS.length);
        // Excel does not grow a row to fit a merged cell's wrapped text, so the row is sized here.
        const lines = caseValueLines(value);
        if (lines > 1) row.height = lines * LINE_HEIGHT;
    });
    ['A9', 'B9', 'C9'].forEach((address) => (sheet.getCell(address).border = { bottom: THIN }));
    sheet.mergeCells('A9:C9');

    const header = sheet.getRow(HEADER_ROW);
    header.values = TABLE_LABELS.map(label);
    header.height = 41.1;
    data.rows.forEach((row, index) => (sheet.getRow(HEADER_ROW + 1 + index).values = tableRow(row)));
    for (let rowNumber = HEADER_ROW; rowNumber <= HEADER_ROW + data.rows.length; rowNumber++) {
        for (let column = 1; column <= TABLE_LABELS.length; column++) {
            const cell = sheet.getRow(rowNumber).getCell(column);
            cell.border = BOXED;
            cell.alignment = { wrapText: true, vertical: 'top' };
        }
    }

    // One blank row after the table, then the footer, its law-firm lines kept apart by one more.
    const footerStart = HEADER_ROW + data.rows.length + 2;
    const firmAddress = [data.lawFirmAddress, data.lawFirmCityName, data.lawFirmCountryName].filter(Boolean);
    const footer: [number, string, string][] = [
        [footerStart, 'insider.matter_changed_to_confidential', formatLocalMinute(data.markedAsInsider)],
        [footerStart + 2, 'insider.law_firm_name', data.lawFirmName ?? ''],
        [footerStart + 3, 'insider.law_firm_addess', firmAddress.join(', ')],
    ];
    footer.forEach(([rowNumber, placeholder, value]) => {
        const row = sheet.getRow(rowNumber);
        row.getCell(1).value = `${label(placeholder)}:`;
        row.getCell(4).value = value;
        [row.getCell(1), row.getCell(4)].forEach((cell) => (cell.alignment = TOP));
    });
    sheet.getRow(footerStart + 4).getCell(1).value = label('insider.disclaimer_of_timezone');
    return sheet;
}

/**
 * Downloads the insider list as `<case number>_insider_list.xlsx`, or with the day of a dated version appended.
 */
export async function exportInsiderListXlsx(data: MatterInsiderListExport, asOfDate?: string): Promise<void> {
    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    buildInsiderListSheet(workbook, data, new Date());
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${[data.caseNumber, 'insider_list', asOfDate].filter(Boolean).join('_')}.xlsx`);
}
